import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Map,
  APIProvider,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import Header from "../../layout/header";
import { createLmdTerritory, updateLmdTerritory, getLmdTerritory, getAllLmdTerritories, deleteLmdTerritory } from "../../api/territoryMap";
import { toast } from "react-toastify";

interface Territory {
  id: string;
  name: string;
  area: string;
  coordinates: google.maps.LatLngLiteral[];
  color: string;
  apiId?: string | number; // Store the API territory ID
  comment?: string; // Store comment from API
  territory_id?: string | number;
  city?: string;
  country?: string;
  status?: string; // 'active' or 'inactive'
}

const saudiCenter = { lat: 24.774265, lng: 46.738586 };
const saudiBounds = {
  north: 33.0,
  south: 16.0,
  east: 60.0,
  west: 34.0,
};

// Helper function to safely convert coordinates to numbers
const parseCoordinate = (value: any): number => {
  if (typeof value === 'number' && isFinite(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  if (isNaN(parsed) || !isFinite(parsed)) {
    console.warn("Invalid coordinate value:", value);
    return 0;
  }
  return parsed;
};

// Helper function to lighten a hex color
const lightenColor = (color: string, percent: number): string => {
  // Remove # if present
  const hex = color.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Lighten by increasing each component towards white (255)
  const newR = Math.min(255, Math.floor(r + (255 - r) * percent));
  const newG = Math.min(255, Math.floor(g + (255 - g) * percent));
  const newB = Math.min(255, Math.floor(b + (255 - b) * percent));
  
  // Convert back to hex
  const toHex = (n: number) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// Helper function to convert API coordinate to LatLngLiteral
const convertToLatLngLiteral = (coord: any): google.maps.LatLngLiteral | null => {
  if (!coord || (typeof coord.lat === 'undefined' && typeof coord.lng === 'undefined')) {
    console.warn("Invalid coordinate object:", coord);
    return null;
  }
  
  const lat = parseCoordinate(coord.lat);
  const lng = parseCoordinate(coord.lng);
  
  if (lat === 0 && lng === 0 && (coord.lat !== 0 || coord.lng !== 0)) {
    // This might be a real 0,0 coordinate, but log it to be safe
    console.warn("Coordinate parsed to 0,0 from:", coord);
  }
  
  return { lat, lng };
};

// Helper function to check if a point is inside a polygon using ray casting algorithm
const isPointInPolygon = (point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;
    
    const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
      (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

// Helper function to check if two polygons overlap
const checkPolygonOverlap = (
  newPolygon: google.maps.LatLngLiteral[],
  existingTerritories: Territory[]
): { overlaps: boolean; overlappingTerritory?: Territory } => {
  // Check if any vertex of the new polygon is inside any existing polygon
  for (const territory of existingTerritories) {
    // Check if any point of new polygon is inside existing polygon
    for (const point of newPolygon) {
      if (isPointInPolygon(point, territory.coordinates)) {
        return { overlaps: true, overlappingTerritory: territory };
      }
    }
    
    // Check if any point of existing polygon is inside new polygon
    for (const point of territory.coordinates) {
      if (isPointInPolygon(point, newPolygon)) {
        return { overlaps: true, overlappingTerritory: territory };
      }
    }
  }
  
  return { overlaps: false };
};

const DrawingControls: React.FC<{
  onDrawingModeChange: (mode: google.maps.drawing.OverlayType | null) => void;
  drawingMode: google.maps.drawing.OverlayType | null;
  onSaveTerritory: (name: string, coordinates: google.maps.LatLngLiteral[], comment: string) => void;
  territories: Territory[];
  onDeleteTerritory: (id: string) => void;
  onSelectTerritory: (id: string | null) => void;
  selectedTerritory: string | null;
  tempPolygon: google.maps.LatLngLiteral[] | null;
  tempPolygonOverlap: { overlaps: boolean; overlappingTerritory?: Territory } | null;
  onCancelDrawing: () => void;
  editingTerritory: string | null;
  onStartEdit: (id: string | null) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  isLoading?: boolean;
  isLoadingTerritories?: boolean;
}> = ({
  onDrawingModeChange,
  drawingMode,
  onSaveTerritory,
  territories,
  onDeleteTerritory,
  onSelectTerritory,
  selectedTerritory,
  tempPolygon,
  tempPolygonOverlap,
  onCancelDrawing,
  editingTerritory,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  isLoading = false,
  isLoadingTerritories = false,
}) => {
  const [showNameInput, setShowNameInput] = useState(false);
  const [territoryName, setTerritoryName] = useState("");
  const [territoryComment, setTerritoryComment] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setMenuOpen(null);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const toggleMenu = (territoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(menuOpen === territoryId ? null : territoryId);
  };

  useEffect(() => {
    if (tempPolygon) {
      setShowNameInput(true);
    } else {
      setShowNameInput(false);
      setTerritoryName("");
      setTerritoryComment("");
    }
  }, [tempPolygon]);

  const handleSave = () => {
    if (territoryName.trim() && tempPolygon) {
      onSaveTerritory(territoryName, tempPolygon, territoryComment);
      setTerritoryName("");
      setTerritoryComment("");
      setShowNameInput(false);
    }
  };

  const handleCancel = () => {
    setTerritoryName("");
    setTerritoryComment("");
    setShowNameInput(false);
    onCancelDrawing();
  };

  return (
    <div className="w-full h-full bg-white p-4 overflow-y-auto">
      <h2 className="text-lg font-bold mb-4">LMD Territory</h2>
      
      {!showNameInput ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => {
                const google = (window as any).google;
                const POLYGON_TYPE = google?.maps?.drawing?.OverlayType?.POLYGON || null;
                if (drawingMode === POLYGON_TYPE) {
                  onDrawingModeChange(null);
                } else {
                  onDrawingModeChange(POLYGON_TYPE);
                }
              }}
              className={`px-3 py-1.5 text-sm rounded ${
                drawingMode !== null
                  ? "bg-purple text-white"
                  : "bg-purple text-white hover:bg-purple"
              }`}
            >
              {drawingMode !== null ? "Stop Drawing" : "Draw Territory"}
            </button>
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search territories..."
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple"
              />
              <button
                onClick={() => {
                  // Search functionality - filtering is handled in the map function
                }}
                className="px-4 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
              >
                Search
              </button>
            </div>
          </div>

          {isLoadingTerritories ? (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Saved Territories</h3>
              <div className="text-center py-4 text-gray-500">Loading territories...</div>
            </div>
          ) : territories.length > 0 ? (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Saved Territories</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-base">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left p-2 font-semibold text-sm">ID</th>
                      <th className="text-left p-2 font-semibold text-sm">Name</th>
                      <th className="text-left p-2 font-semibold text-sm">Area</th>
                      <th className="text-left p-2 font-semibold text-sm">City</th>
                      <th className="text-left p-2 font-semibold text-sm">Country</th>
                      <th className="text-left p-2 font-semibold text-sm">Status</th>
                      <th className="text-left p-2 font-semibold text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {territories
                      .filter((territory) => {
                        if (!searchTerm) return true;
                        const search = searchTerm.toLowerCase();
                        return (
                          territory.name?.toLowerCase().includes(search) ||
                          territory.territory_id?.toString().toLowerCase().includes(search) ||
                          territory.city?.toLowerCase().includes(search) ||
                          territory.country?.toLowerCase().includes(search) ||
                          territory.status?.toLowerCase().includes(search)
                        );
                      })
                      .map((territory) => (
                      <tr
                        key={territory.id}
                        className={`border-b border-gray-200 hover:bg-purple-50 ${
                          selectedTerritory === territory.id ? "bg-purple-100" : ""
                        } ${editingTerritory === territory.id ? "" : "cursor-pointer"}`}
                        onClick={() => {
                          if (editingTerritory !== territory.id) {
                            onSelectTerritory(territory.id === selectedTerritory ? null : territory.id);
                          }
                        }}
                      >
                        <td className="p-2 text-sm">{territory.territory_id || territory.apiId || "-"}</td>
                        <td className="p-2 text-sm font-medium">{territory.name}</td>
                        <td className="p-2 text-sm">{territory.area ? `${territory.area} sq m` : "-"}</td>
                        <td className="p-2 text-sm">{territory.city || "-"}</td>
                        <td className="p-2 text-sm">{territory.country || "-"}</td>
                        <td className="p-2">
                          <span
                            className={`px-2 py-1 rounded text-sm font-medium ${
                              territory.status?.toLowerCase() === "active"
                                ? "bg-green-100 text-green-800"
                                : territory.status?.toLowerCase() === "inactive"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {territory.status || "N/A"}
                          </span>
                        </td>
                        <td className="p-2 relative">
                          {editingTerritory === territory.id ? (
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSaveEdit();
                                }}
                                disabled={isLoading}
                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                title="Save Changes"
                              >
                                {isLoading ? "Saving..." : "Save"}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelEdit();
                                }}
                                disabled={isLoading}
                                className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                title="Cancel"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="relative">
                              <button
                                onClick={(e) => toggleMenu(territory.id, e)}
                                className="text-gray-500 hover:text-gray-800 p-1"
                                title="Actions"
                              >
                                <span className="text-lg">⋮</span>
                              </button>
                              {menuOpen === territory.id && (
                                <div
                                  ref={dropdownRef}
                                  className="absolute right-0 -mt-2 w-32 bg-white border border-gray-200 shadow-lg rounded z-10 text-left"
                                >
                                  <ul className="py-1 text-sm text-gray-900">
                                    <li
                                      className="px-4 py-2 hover:bg-gray-200 cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onStartEdit(territory.id);
                                        setMenuOpen(null);
                                      }}
                                    >
                                      Edit
                                    </li>
                                    <li
                                      className="px-4 py-2 hover:bg-gray-200 cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteTerritory(territory.id);
                                        setMenuOpen(null);
                                      }}
                                    >
                                      Delete
                                    </li>
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Saved Territories</h3>
              <div className="text-center py-4 text-gray-500 text-sm">No territories saved yet</div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          {tempPolygonOverlap?.overlaps && (
            <div className="p-3 bg-red-50 border border-red-300 rounded text-red-700 text-sm">
              <div className="font-semibold mb-1">⚠️ Overlap Detected!</div>
              <div>
                This territory overlaps with "{tempPolygonOverlap.overlappingTerritory?.name || "another territory"}".
                Please adjust the boundaries before saving.
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Territory Name</label>
            <input
              type="text"
              value={territoryName}
              onChange={(e) => setTerritoryName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.ctrlKey) {
                  handleSave();
                } else if (e.key === "Escape") {
                  handleCancel();
                }
              }}
              placeholder="Enter territory name"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Comment (Optional)</label>
            <textarea
              value={territoryComment}
              onChange={(e) => setTerritoryComment(e.target.value)}
              placeholder="Enter comment"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!territoryName.trim() || isLoading || (tempPolygonOverlap?.overlaps === true)}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MapDrawingManager: React.FC<{
  drawingMode: google.maps.drawing.OverlayType | null;
  onPolygonComplete: (coordinates: google.maps.LatLngLiteral[]) => void;
}> = ({ drawingMode, onPolygonComplete }) => {
  const map = useMap();
  const drawingLibrary = useMapsLibrary("drawing");
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const polygonCompleteCallbackRef = useRef(onPolygonComplete);

  useEffect(() => {
    polygonCompleteCallbackRef.current = onPolygonComplete;
  }, [onPolygonComplete]);

  useEffect(() => {
    if (!map || !drawingLibrary) return;

    if (!drawingManagerRef.current) {
      const DrawingManager = drawingLibrary.DrawingManager;
      const drawingManager = new DrawingManager({
        map: map,
        drawingMode: null,
        drawingControl: false,
        polygonOptions: {
          fillColor: "#4285F4",
          fillOpacity: 0.35,
          strokeWeight: 4,
          strokeColor: "#4285F4",
          clickable: false,
          editable: false,
          zIndex: 1,
        },
      });

      drawingManager.addListener("polygoncomplete", (polygon: google.maps.Polygon) => {
        const path = polygon.getPath();
        const coordinates: google.maps.LatLngLiteral[] = [];
        path.forEach((latLng) => {
          coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
        });
        polygon.setMap(null); // Remove the temporary polygon from the map
        polygonCompleteCallbackRef.current(coordinates);
      });

      drawingManagerRef.current = drawingManager;
    }

    return () => {
      if (drawingManagerRef.current) {
        const google = (window as any).google;
        if (google?.maps?.event) {
          google.maps.event.clearInstanceListeners(drawingManagerRef.current);
        }
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }
    };
  }, [map, drawingLibrary]);

  useEffect(() => {
    if (drawingManagerRef.current) {
      if (drawingMode !== null) {
        drawingManagerRef.current.setDrawingMode(drawingMode);
      } else {
        drawingManagerRef.current.setDrawingMode(null);
      }
    }
  }, [drawingMode]);

  return null;
};

const MapClickHandler: React.FC<{
  onMapClick: () => void;
}> = ({ onMapClick }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    // Listen for clicks on the map (empty space, not on polygons)
    const clickListener = map.addListener("click", () => {
      onMapClick();
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [map, onMapClick]);

  return null;
};

const TerritoryPolygons: React.FC<{
  territories: Territory[];
  selectedTerritory: string | null;
  tempPolygon: google.maps.LatLngLiteral[] | null;
  tempPolygonOverlap: { overlaps: boolean; overlappingTerritory?: Territory } | null;
  onTerritoryClick: (id: string) => void;
  editingTerritory: string | null;
  onTerritoryUpdate: (id: string, coordinates: google.maps.LatLngLiteral[]) => void;
  editingCoordinates: Record<string, google.maps.LatLngLiteral[]>;
}> = ({ territories, selectedTerritory, tempPolygon, tempPolygonOverlap, onTerritoryClick, editingTerritory, onTerritoryUpdate, editingCoordinates }) => {
  const map = useMap();
  const polygonsRef = useRef<Record<string, google.maps.Polygon>>({});
  const tempPolygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map) return;

    // Wait for google.maps to be available
    const initPolygons = () => {
      const google = (window as any).google;
      if (!google?.maps?.Polygon) {
        setTimeout(initPolygons, 100);
        return;
      }

      console.log("TerritoryPolygons: Rendering", territories.length, "territories");
      console.log("TerritoryPolygons: editingTerritory =", editingTerritory);
      console.log("TerritoryPolygons: territories =", territories.map(t => ({ id: t.id, name: t.name, coordsLength: t.coordinates.length })));

      // Clear existing polygons and listeners
      Object.values(polygonsRef.current).forEach((polygon) => {
        // Remove path listeners if they exist
        const listeners = (polygon as any).__updateListeners;
        if (listeners) {
          listeners.forEach((listener: google.maps.MapsEventListener) => {
            google.maps.event.removeListener(listener);
          });
        }
        polygon.setMap(null);
      });
      polygonsRef.current = {};

      // Create polygons for each territory
      territories.forEach((territory) => {
        // Validate coordinates
        if (!territory.coordinates || !Array.isArray(territory.coordinates) || territory.coordinates.length === 0) {
          console.warn("Territory has invalid coordinates:", territory);
          return;
        }

        const isEditing = editingTerritory === territory.id;
        // Use editingCoordinates if available and territory is being edited, otherwise use territory coordinates
        let coordinates = isEditing && editingCoordinates[territory.id] && Array.isArray(editingCoordinates[territory.id])
          ? editingCoordinates[territory.id] 
          : territory.coordinates;

        // Validate and sanitize coordinates - ensure all are valid numbers
        coordinates = coordinates
          .map((coord: google.maps.LatLngLiteral) => ({
            lat: parseCoordinate(coord.lat),
            lng: parseCoordinate(coord.lng),
          }))
          .filter((coord: google.maps.LatLngLiteral) => 
            isFinite(coord.lat) && isFinite(coord.lng)
          );

        if (coordinates.length < 3) {
          console.warn(`Territory ${territory.name} has insufficient valid coordinates: ${coordinates.length}`);
          return; // Skip polygon creation if not enough valid coordinates
        }

        console.log(`Creating polygon for ${territory.name}: ${coordinates.length} coordinates`);

        const polygon = new google.maps.Polygon({
          paths: coordinates,
          fillColor: territory.color,
          fillOpacity: selectedTerritory === territory.id ? 0.5 : 0.3,
          strokeWeight: selectedTerritory === territory.id ? 5 : 4,
          strokeColor: selectedTerritory === territory.id ? lightenColor(territory.color, 0.2) : territory.color,
          clickable: !isEditing,
          editable: isEditing,
          draggable: false,
          zIndex: selectedTerritory === territory.id ? 10 : 1,
          map: map,
        });

        polygon.addListener("click", (e: google.maps.MapMouseEvent) => {
          if (!isEditing) {
            e.stop(); // Stop event propagation to prevent map click handler from firing
            onTerritoryClick(territory.id);
          }
        });

        // Listen for path changes when editing
        if (isEditing) {
          const path = polygon.getPath();
          const updateListener = path.addListener("set_at", () => {
            const coordinates: google.maps.LatLngLiteral[] = [];
            path.forEach((latLng: google.maps.LatLng) => {
              coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
            });
            onTerritoryUpdate(territory.id, coordinates);
          });
          
          const insertListener = path.addListener("insert_at", () => {
            const coordinates: google.maps.LatLngLiteral[] = [];
            path.forEach((latLng: google.maps.LatLng) => {
              coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
            });
            onTerritoryUpdate(territory.id, coordinates);
          });

          const removeListener = path.addListener("remove_at", () => {
            const coordinates: google.maps.LatLngLiteral[] = [];
            path.forEach((latLng: google.maps.LatLng) => {
              coordinates.push({ lat: latLng.lat(), lng: latLng.lng() });
            });
            onTerritoryUpdate(territory.id, coordinates);
          });

          // Store listeners for cleanup
          (polygon as any).__updateListeners = [updateListener, insertListener, removeListener];
        }

        polygonsRef.current[territory.id] = polygon;
      });
    };

    initPolygons();

    return () => {
      Object.values(polygonsRef.current).forEach((polygon) => {
        const listeners = (polygon as any).__updateListeners;
        if (listeners) {
          listeners.forEach((listener: google.maps.MapsEventListener) => {
            google.maps.event.removeListener(listener);
          });
        }
        polygon.setMap(null);
      });
      polygonsRef.current = {};
    };

    initPolygons();
  }, [map, territories, selectedTerritory, editingTerritory, editingCoordinates, onTerritoryClick, onTerritoryUpdate]);

  useEffect(() => {
    if (!map) return;

    // Handle temp polygon
    const initTempPolygon = () => {
      const google = (window as any).google;
      if (!google?.maps?.Polygon) {
        if (tempPolygon) {
          setTimeout(initTempPolygon, 100);
        }
        return;
      }

      if (tempPolygon) {
        if (tempPolygonRef.current) {
          tempPolygonRef.current.setMap(null);
        }
        // Change color to red if overlapping, blue otherwise
        const isOverlapping = tempPolygonOverlap?.overlaps === true;
        const fillColor = isOverlapping ? "#EA4335" : "#4285F4";
        const strokeColor = isOverlapping ? "#EA4335" : "#4285F4";
        tempPolygonRef.current = new google.maps.Polygon({
          paths: tempPolygon,
          fillColor: fillColor,
          fillOpacity: 0.35,
          strokeWeight: isOverlapping ? 5 : 4,
          strokeColor: strokeColor,
          clickable: false,
          zIndex: 5,
          map: map,
        });
      } else {
        if (tempPolygonRef.current) {
          tempPolygonRef.current.setMap(null);
          tempPolygonRef.current = null;
        }
      }
    };

    initTempPolygon();

    return () => {
      if (tempPolygonRef.current) {
        tempPolygonRef.current.setMap(null);
        tempPolygonRef.current = null;
      }
    };
  }, [map, tempPolygon, tempPolygonOverlap]);

  return null;
};

const TerritoryMapView: React.FC = () => {
  const [drawingMode, setDrawingMode] = useState<google.maps.drawing.OverlayType | null>(null);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [selectedTerritory, setSelectedTerritory] = useState<string | null>(null);
  const [tempPolygon, setTempPolygon] = useState<google.maps.LatLngLiteral[] | null>(null);
  const [tempPolygonOverlap, setTempPolygonOverlap] = useState<{ overlaps: boolean; overlappingTerritory?: Territory } | null>(null);
  const [editingTerritory, setEditingTerritory] = useState<string | null>(null);
  const [editingCoordinates, setEditingCoordinates] = useState<Record<string, google.maps.LatLngLiteral[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTerritories, setIsLoadingTerritories] = useState(true);
  const nextIdRef = useRef(1);

  // Check for overlap in real-time when tempPolygon changes
  useEffect(() => {
    if (tempPolygon && tempPolygon.length >= 3) {
      const overlapCheck = checkPolygonOverlap(tempPolygon, territories);
      setTempPolygonOverlap(overlapCheck);
      
      if (overlapCheck.overlaps) {
        const territoryName = overlapCheck.overlappingTerritory?.name || "another territory";
        toast.warning(`⚠️ Territory overlaps with "${territoryName}"! Please adjust the boundaries.`, {
          autoClose: 5000,
        });
      }
    } else {
      setTempPolygonOverlap(null);
    }
  }, [tempPolygon, territories]);

  // Load territories from API on mount
  useEffect(() => {
    const fetchTerritories = async () => {
      setIsLoadingTerritories(true);
      try {
        const apiTerritories = await getAllLmdTerritories();
        
        console.log("API Territories received:", apiTerritories);
        
        // Check if we received data
        if (!apiTerritories || !Array.isArray(apiTerritories)) {
          console.warn("API returned invalid data structure:", apiTerritories);
          setTerritories([]);
          setIsLoadingTerritories(false);
          return;
        }

        // Check if array is empty
        if (apiTerritories.length === 0) {
          console.log("No territories found in API response");
          setTerritories([]);
          setIsLoadingTerritories(false);
          return;
        }
        
        const colors = [
          "#4285F4",
          "#EA4335",
          "#FBBC05",
          "#34A853",
          "#9C27B0",
          "#FF9800",
          "#00BCD4",
          "#E91E63",
        ];

        // Convert API response to Territory format
        const formattedTerritories: Territory[] = apiTerritories
          .filter((apiTerritory) => {
            // Filter out invalid territories
            if (!apiTerritory || !apiTerritory.coordinates || !Array.isArray(apiTerritory.coordinates)) {
              console.warn("Invalid territory data:", apiTerritory);
              return false;
            }
            return true;
          })
          .map((apiTerritory, index) => {
            // Convert API coordinates to google.maps.LatLngLiteral format
            const coordinates = apiTerritory.coordinates
              .map((coord: any) => convertToLatLngLiteral(coord))
              .filter((coord: google.maps.LatLngLiteral | null): coord is google.maps.LatLngLiteral => coord !== null);

              return {
                id: `territory-${apiTerritory.id || index}`,
                name: apiTerritory.territory_name || `Territory ${index + 1}`,
                coordinates,
                color: colors[index % colors.length],
                apiId: apiTerritory.id,
                comment: apiTerritory.comment || "",
                area: apiTerritory.area || "",
                territory_id: apiTerritory.territory_id || apiTerritory.id,
                city: apiTerritory.city || "",
                country: apiTerritory.country || "",
                status: apiTerritory.status || "",
              };
          });

        console.log("Formatted territories:", formattedTerritories);
        setTerritories(formattedTerritories);
        
        // Update nextIdRef
        if (formattedTerritories.length > 0) {
          const maxId = Math.max(...formattedTerritories.map((t: Territory) => {
            const match = t.id.match(/\d+/);
            return match ? parseInt(match[0]) : 0;
          }));
          nextIdRef.current = maxId + 1;
        }
      } catch (error: any) {
        console.error("Error fetching territories:", error);
        toast.error(error?.response?.data?.message || "Failed to load territories");
        setTerritories([]);
      } finally {
        setIsLoadingTerritories(false);
      }
    };

    fetchTerritories();
  }, []);

  const handlePolygonComplete = useCallback((coordinates: google.maps.LatLngLiteral[]) => {
    setTempPolygon(coordinates);
    setDrawingMode(null);
  }, []);

  const handleSaveTerritory = useCallback(async (name: string, coordinates: google.maps.LatLngLiteral[], comment: string) => {
    // Validate coordinates first
    if (!coordinates || coordinates.length < 3) {
      toast.error("A territory must have at least 3 coordinates");
      return;
    }

    // Check for overlap with existing territories
    const overlapCheck = checkPolygonOverlap(coordinates, territories);
    if (overlapCheck.overlaps) {
      const territoryName = overlapCheck.overlappingTerritory?.name || "another territory";
      toast.error(`Cannot create territory: The new territory overlaps with "${territoryName}". Please draw a territory that does not overlap with existing territories.`);
      return;
    }

    setIsLoading(true);
    try {
      // Convert coordinates to API format
      const apiCoordinates = coordinates.map(coord => ({
        lat: coord.lat,
        lng: coord.lng,
      }));

      // Call API to create territory
      await createLmdTerritory({
        territory_name: name,
        coordinates: apiCoordinates,
        comment: comment || "",
      });

      // Refresh territories list from API to ensure sync
      const apiTerritories = await getAllLmdTerritories();
      const colors = [
        "#4285F4",
        "#EA4335",
        "#FBBC05",
        "#34A853",
        "#9C27B0",
        "#FF9800",
        "#00BCD4",
        "#E91E63",
      ];
      const formattedTerritories: Territory[] = apiTerritories
        .filter((apiTerritory) => {
          // Filter out invalid territories
          if (!apiTerritory || !apiTerritory.coordinates || !Array.isArray(apiTerritory.coordinates)) {
            console.warn("Invalid territory data:", apiTerritory);
            return false;
          }
          return true;
        })
        .map((apiTerritory, index) => {
          const coords = apiTerritory.coordinates
            .map((coord: any) => convertToLatLngLiteral(coord))
            .filter((coord: google.maps.LatLngLiteral | null): coord is google.maps.LatLngLiteral => coord !== null);
          
          if (coords.length === 0) {
            console.warn("Territory has no valid coordinates:", apiTerritory);
          }
          
          return {
          id: `territory-${apiTerritory.id || index}`,
          name: apiTerritory.territory_name,
          coordinates: coords,
          color: colors[index % colors.length],
          apiId: apiTerritory.id,
          comment: apiTerritory.comment,
          area: apiTerritory.area || "",
          territory_id: apiTerritory.territory_id || apiTerritory.id,
          city: apiTerritory.city || "",
          country: apiTerritory.country || "",
          status: apiTerritory.status || "",
        };
      });
      setTerritories(formattedTerritories);
      setTempPolygon(null);
      // Clear editing state after territories are set to prevent render issues
      setEditingTerritory(null);
      setEditingCoordinates({});
      toast.success("Territory created successfully!");
    } catch (error: any) {
      console.error("Error creating territory:", error);
      toast.error(error?.response?.data?.message || "Failed to create territory");
    } finally {
      setIsLoading(false);
    }
  }, [territories]);

  const handleDeleteTerritory = useCallback(async (id: string) => {
    const territory = territories.find(t => t.id === id);
    if (!territory) {
      toast.error("Territory not found");
      return;
    }

    // If territory has an API ID, delete via API
    if (territory.apiId) {
      try {
        await deleteLmdTerritory(territory.apiId);
        
        // Refresh territories list from API to ensure sync
        const apiTerritories = await getAllLmdTerritories();
        const colors = [
          "#4285F4",
          "#EA4335",
          "#FBBC05",
          "#34A853",
          "#9C27B0",
          "#FF9800",
          "#00BCD4",
          "#E91E63",
        ];
        const formattedTerritories: Territory[] = apiTerritories
          .filter((apiTerritory) => {
            if (!apiTerritory || !apiTerritory.coordinates || !Array.isArray(apiTerritory.coordinates)) {
              console.warn("Invalid territory data:", apiTerritory);
              return false;
            }
            return true;
          })
          .map((apiTerritory, index) => {
            const coordinates = apiTerritory.coordinates
              .map((coord: any) => convertToLatLngLiteral(coord))
              .filter((coord: google.maps.LatLngLiteral | null): coord is google.maps.LatLngLiteral => coord !== null);
            
            return {
              id: `territory-${apiTerritory.id || index}`,
              name: apiTerritory.territory_name || `Territory ${index + 1}`,
              coordinates,
              color: colors[index % colors.length],
              apiId: apiTerritory.id,
              comment: apiTerritory.comment || "",
              area: apiTerritory.area || "",
              territory_id: apiTerritory.territory_id || apiTerritory.id,
              city: apiTerritory.city || "",
              country: apiTerritory.country || "",
              status: apiTerritory.status || "",
            };
          });
        setTerritories(formattedTerritories);
        
        if (selectedTerritory === id) {
          setSelectedTerritory(null);
        }
        
        toast.success("Territory deleted successfully!");
      } catch (error: any) {
        console.error("Error deleting territory:", error);
        toast.error(error?.response?.data?.message || "Failed to delete territory");
      }
    } else {
      // If no API ID, just remove locally
      setTerritories((prev) => prev.filter((t) => t.id !== id));
      if (selectedTerritory === id) {
        setSelectedTerritory(null);
      }
    }
  }, [selectedTerritory, territories]);

  const handleCancelDrawing = useCallback(() => {
    setTempPolygon(null);
    setDrawingMode(null);
  }, []);

  const handleStartEdit = useCallback((id: string | null) => {
    if (id) {
      const territory = territories.find(t => t.id === id);
      if (territory) {
        setEditingCoordinates({ [id]: [...territory.coordinates] });
        setEditingTerritory(id);
        setDrawingMode(null);
      }
    }
  }, [territories]);

  const handleSaveEdit = useCallback(async () => {
    if (editingTerritory && editingCoordinates[editingTerritory]) {
      const editedCoordinates = editingCoordinates[editingTerritory];
      
      // Validate coordinates
      if (!editedCoordinates || editedCoordinates.length < 3) {
        toast.error("A territory must have at least 3 coordinates");
        return;
      }

      // Check for overlap with other territories (excluding the current one being edited)
      const otherTerritories = territories.filter(t => t.id !== editingTerritory);
      const overlapCheck = checkPolygonOverlap(editedCoordinates, otherTerritories);
      if (overlapCheck.overlaps) {
        const territoryName = overlapCheck.overlappingTerritory?.name || "another territory";
        toast.error(`Cannot update territory: The edited territory overlaps with "${territoryName}". Please modify the territory so it does not overlap with other territories.`);
        return;
      }

      setIsLoading(true);
      try {
        const territory = territories.find(t => t.id === editingTerritory);
        if (!territory) {
          toast.error("Territory not found");
          setIsLoading(false);
          return;
        }

        // If territory has an API ID, update via API
        if (territory.apiId) {
          const apiCoordinates = editingCoordinates[editingTerritory].map(coord => ({
            lat: coord.lat,
            lng: coord.lng,
          }));

          const response = await updateLmdTerritory(territory.apiId, {
            territory_name: territory.name,
            coordinates: apiCoordinates,
            comment: territory.comment || "",
          });

          // Refresh territories list from API to ensure sync
          try {
            const apiTerritories = await getAllLmdTerritories();
            const colors = [
              "#4285F4",
              "#EA4335",
              "#FBBC05",
              "#34A853",
              "#9C27B0",
              "#FF9800",
              "#00BCD4",
              "#E91E63",
            ];
            const formattedTerritories: Territory[] = apiTerritories
              .filter((apiTerritory) => {
                // Filter out invalid territories
                if (!apiTerritory || !apiTerritory.coordinates || !Array.isArray(apiTerritory.coordinates)) {
                  console.warn("Invalid territory data:", apiTerritory);
                  return false;
                }
                return true;
              })
              .map((apiTerritory, index) => {
                const coordinates = apiTerritory.coordinates
                  .map((coord: any) => convertToLatLngLiteral(coord))
                  .filter((coord: google.maps.LatLngLiteral | null): coord is google.maps.LatLngLiteral => coord !== null);
                
                if (coordinates.length === 0) {
                  console.warn("Territory has no valid coordinates:", apiTerritory);
                }
                
                return {
                id: `territory-${apiTerritory.id || index}`,
                name: apiTerritory.territory_name,
                coordinates,
                color: colors[index % colors.length],
                apiId: apiTerritory.id,
                comment: apiTerritory.comment,
                area: apiTerritory.area || "",
                territory_id: apiTerritory.territory_id || apiTerritory.id,
                city: apiTerritory.city || "",
                country: apiTerritory.country || "",
                status: apiTerritory.status || "",
              };
            });
            setTerritories(formattedTerritories);
            // Clear editing state after territories are set to prevent render issues
            setEditingTerritory(null);
            setEditingCoordinates({});
            toast.success("Territory updated successfully!");
          } catch (refreshError) {
            // If refresh fails, update locally
            const currentEditingId = editingTerritory;
            const validCoordinates = response.coordinates
              ? response.coordinates
                  .map((coord: any) => convertToLatLngLiteral(coord))
                  .filter((coord: google.maps.LatLngLiteral | null): coord is google.maps.LatLngLiteral => coord !== null)
              : [];
            
            setTerritories((prev) =>
              prev.map((t) =>
                t.id === currentEditingId
                  ? { 
                      ...t, 
                      coordinates: validCoordinates.length > 0 ? validCoordinates : t.coordinates,
                      comment: response.comment || t.comment,
                    }
                  : t
              )
            );
            // Clear editing state after territories are set
            setEditingTerritory(null);
            setEditingCoordinates({});
            toast.success("Territory updated successfully!");
            console.error("Error refreshing territories:", refreshError);
          }
        } else {
          // If no API ID, just update locally
          // Store values before clearing editing state
          const currentEditingId = editingTerritory;
          const currentEditingCoords = editingCoordinates[editingTerritory];
          
          setTerritories((prev) =>
            prev.map((t) =>
              t.id === currentEditingId
                ? { ...t, coordinates: currentEditingCoords }
                : t
            )
          );
          // Clear editing state after territories are set to prevent render issues
          setEditingTerritory(null);
          setEditingCoordinates({});
        }
      } catch (error: any) {
        console.error("Error updating territory:", error);
        toast.error(error?.response?.data?.message || "Failed to update territory");
      } finally {
        setIsLoading(false);
      }
    }
  }, [editingTerritory, editingCoordinates, territories]);

  const handleCancelEdit = useCallback(() => {
    setEditingTerritory(null);
    setEditingCoordinates({});
  }, []);

  const handleTerritoryUpdate = useCallback((id: string, coordinates: google.maps.LatLngLiteral[]) => {
    setEditingCoordinates((prev) => ({
      ...prev,
      [id]: coordinates,
    }));
  }, []);

  return (
    <div className="flex flex-col h-screen">
      <div className="w-full fixed z-50">
        <Header />
      </div>
      <div className="flex-1 pt-[54px] flex flex-row">
        {/* Sidebar */}
        <div className="w-[40%] border-r border-gray-300 flex-shrink-0">
          <DrawingControls
            onDrawingModeChange={setDrawingMode}
            drawingMode={drawingMode}
            onSaveTerritory={handleSaveTerritory}
            territories={territories}
            onDeleteTerritory={handleDeleteTerritory}
            onSelectTerritory={setSelectedTerritory}
            selectedTerritory={selectedTerritory}
            tempPolygon={tempPolygon}
            tempPolygonOverlap={tempPolygonOverlap}
            onCancelDrawing={handleCancelDrawing}
            editingTerritory={editingTerritory}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            isLoading={isLoading}
            isLoadingTerritories={isLoadingTerritories}
          />
        </div>

        {/* Map */}
        <div className="w-[60%] relative">
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY || ""}>
            <Map
              style={{ width: "100%", height: "100%" }}
              defaultCenter={saudiCenter}
              defaultZoom={7}
              gestureHandling={"greedy"}
              disableDefaultUI={true}
              minZoom={6}
              maxZoom={18}
              zoomControl={true}
              restriction={{
                latLngBounds: saudiBounds,
                strictBounds: true,
              }}
            >
              <MapClickHandler
                onMapClick={() => {
                  if (!editingTerritory) {
                    setSelectedTerritory(null);
                  }
                }}
              />
              <MapDrawingManager
                drawingMode={drawingMode}
                onPolygonComplete={handlePolygonComplete}
              />
              <TerritoryPolygons
                territories={territories}
                selectedTerritory={selectedTerritory}
                tempPolygon={tempPolygon}
                tempPolygonOverlap={tempPolygonOverlap}
                onTerritoryClick={(id) => setSelectedTerritory(id === selectedTerritory ? null : id)}
                editingTerritory={editingTerritory}
                onTerritoryUpdate={handleTerritoryUpdate}
                editingCoordinates={editingCoordinates}
              />
            </Map>
          </APIProvider>
        </div>
      </div>
    </div>
  );
};

export default TerritoryMapView;

